// Interface strings kept separate from components so more languages can be added later.
export const strings = {
  en: {
    brand: "Brilla",
    productName: "Brilla Bilingual Classroom Activity Coach",
    tagline: "Two Languages. One Classroom. Endless Possibilities.",
    common: {
      loading: "Loading...",
      save: "Save",
      saved: "Saved",
      cancel: "Cancel",
      edit: "Edit",
      delete: "Delete",
      duplicate: "Duplicate",
      print: "Print / PDF",
      generate: "Generate",
      regenerate: "Regenerate",
      back: "Back",
      required: "required",
      optional: "optional",
      search: "Search",
      logout: "Log out",
      favorite: "Favorite",
      favorited: "Favorited",
      archive: "Archive",
    },
    nav: {
      dashboard: "Dashboard",
      myActivities: "My Activities",
      askBrilla: "Ask Brilla",
      profile: "My Classroom",
    },
    landing: {
      heroTitle: "Bring Two Languages to Life in Your Classroom",
      heroSubtitle:
        "Brilla helps early childhood educators create meaningful, developmentally appropriate English and Spanish learning experiences in minutes.",
      heroPromise: "Create. Adapt. Teach. Connect.",
      createActivity: "Create an Activity",
      planWeek: "Plan My Week",
      login: "Log in",
      signup: "Get Started",
      developedBy: "Developed by Brilla Educational Consulting LLC",
    },
    auth: {
      loginTitle: "Welcome back",
      loginSubtitle: "Log in to your Brilla account",
      registerTitle: "Create your account",
      registerSubtitle: "Start bringing two languages to life",
      name: "Full name",
      email: "Email",
      password: "Password",
      role: "I am a...",
      roleTeacher: "Teacher",
      roleCoach: "Coach / Education Manager",
      roleAdmin: "Administrator",
      loginBtn: "Log in",
      registerBtn: "Create account",
      forgot: "Forgot password?",
      noAccount: "Don't have an account?",
      haveAccount: "Already have an account?",
      forgotTitle: "Reset your password",
      forgotSubtitle: "Enter your email and we'll send a reset link",
      sendReset: "Send reset link",
      resetTitle: "Set a new password",
      newPassword: "New password",
      resetBtn: "Reset password",
    },
    dashboard: {
      welcome: "Welcome back",
      subtitle: "What would you like to create today?",
      quickActions: "Quick Actions",
      recent: "Recently Created",
      favorites: "Favorites",
      noRecent: "No activities yet. Create your first one!",
      currentClassroom: "Current Classroom",
      ageGroup: "Age Group",
      weeklyTheme: "Weekly Theme",
      notSet: "Not set",
      cards: {
        createActivity: "Create an Activity",
        createActivityDesc: "Generate an individual bilingual activity.",
        planWeek: "Plan My Week",
        planWeekDesc: "Generate connected activities around a weekly theme.",
        makeBilingual: "Make My Activity Bilingual",
        makeBilingualDesc: "Transform an existing activity into a bilingual experience.",
        centers: "Learning Centers",
        centersDesc: "Generate bilingual center activities.",
        book: "Book Companion",
        bookDesc: "Create bilingual activities connected to a book.",
        family: "Family Connection",
        familyDesc: "Generate bilingual home-learning activities.",
        myActivities: "My Activities",
        myActivitiesDesc: "Access your saved activities.",
        weeklyPlans: "My Weekly Plans",
        weeklyPlansDesc: "Access your saved weekly plans.",
        resources: "Resource Library",
        resourcesDesc: "Access bilingual teaching resources.",
      },
      comingSoon: "Coming soon",
    },
    generator: {
      title: "AI Bilingual Activity Generator",
      subtitle: "Tell Brilla about your activity and it will build a complete plan.",
      ageGroup: "Age Group",
      language: "Language",
      theme: "Theme",
      themePlaceholder: "Type your own theme or pick an example below",
      domain: "Learning Domain",
      domainHint: "Choose one or more",
      activityType: "Activity Type",
      duration: "Duration",
      generateBtn: "Generate Activity",
      generating: "Brilla is crafting your activity...",
    },
    activity: {
      objective: "Objective",
      materials: "Materials",
      vocabulary: "Key Vocabulary",
      teacherLanguage: "Teacher Language",
      instructions: "Step-by-Step Instructions",
      questions: "Higher-Order Thinking Questions",
      dll: "DLL Support Strategies",
      differentiation: "Differentiation",
      diffSupport: "Needs additional support",
      diffEnglish: "Beginning English learners",
      diffSpanish: "Beginning Spanish learners",
      diffChallenge: "Needs additional challenge",
      observation: "Observation Opportunities",
      family: "Family Connection",
      teacherSays: "Teacher says",
      disclaimer:
        "AI-generated educational content should be reviewed by the educator before classroom use.",
      saved: "Activity saved to My Activities.",
    },
    makeBilingual: {
      title: "Make My Activity Bilingual",
      subtitle:
        "Paste your existing activity. Brilla won't just translate it — it will design an intentional bilingual learning experience.",
      inputLabel: "Your existing activity",
      inputPlaceholder: "Paste or type your lesson or activity here...",
      ageGroup: "Age Group (optional)",
      transformBtn: "Make it Bilingual",
      original: "Original Activity",
      enhanced: "Brilla Bilingual Enhanced Activity",
    },
    ask: {
      title: "Ask Brilla",
      subtitle: "Your supportive bilingual instructional coach.",
      placeholder: "Ask about bilingual activities, vocabulary, DLL support...",
      send: "Send",
      thinking: "Brilla is thinking...",
      suggestions: [
        "Give me five bilingual questions about insects.",
        "How can I add Spanish vocabulary to my block center?",
        "Make a circle time activity appropriate for toddlers.",
        "Give me a family activity about shapes.",
      ],
    },
    myActivities: {
      title: "My Activities",
      all: "All",
      favorites: "Favorites",
      empty: "No activities found. Try creating one!",
      filters: "Filters",
    },
    profile: {
      title: "My Classroom Profile",
      subtitle: "Brilla uses this to personalize recommendations. Never enter children's names or personal information.",
      teacherName: "Teacher Name",
      classroomName: "Classroom Name",
      program: "Program / Site",
      ageGroup: "Age Group",
      numChildren: "Number of Children",
      primaryLanguage: "Primary Classroom Language",
      languagesSpoken: "Languages Spoken by Children",
      curriculum: "Curriculum Used",
      currentTheme: "Current Theme",
      goals: "Instructional Goals",
    },
    onboarding: {
      next: "Next",
      back: "Back",
      finish: "Get Started",
      skip: "Skip tour",
      steps: [
        { title: "Welcome to Brilla!", body: "Your bilingual classroom planning partner." },
        { title: "Tell Us About Your Classroom", body: "Set your age group, language preferences, and curriculum so Brilla can personalize everything." },
        { title: "Create Your First Activity", body: "See how quickly you can generate a complete bilingual activity." },
        { title: "Make It Your Own", body: "Edit, save, print, and regenerate any activity in seconds." },
        { title: "Ask Brilla", body: "Chat with your AI instructional coach anytime you need ideas or support." },
      ],
    },
  },
  es: {
    brand: "Brilla",
    productName: "Brilla Entrenador de Actividades Bilingües",
    tagline: "Dos idiomas. Un salón. Posibilidades infinitas.",
    common: {
      loading: "Cargando...",
      save: "Guardar",
      saved: "Guardado",
      cancel: "Cancelar",
      edit: "Editar",
      delete: "Eliminar",
      duplicate: "Duplicar",
      print: "Imprimir / PDF",
      generate: "Generar",
      regenerate: "Regenerar",
      back: "Atrás",
      required: "requerido",
      optional: "opcional",
      search: "Buscar",
      logout: "Cerrar sesión",
      favorite: "Favorito",
      favorited: "En favoritos",
      archive: "Archivar",
    },
    nav: {
      dashboard: "Panel",
      myActivities: "Mis Actividades",
      askBrilla: "Pregúntale a Brilla",
      profile: "Mi Salón",
    },
    landing: {
      heroTitle: "Da vida a dos idiomas en tu salón de clases",
      heroSubtitle:
        "Brilla ayuda a los educadores de la primera infancia a crear experiencias de aprendizaje significativas y apropiadas para el desarrollo en inglés y español en minutos.",
      heroPromise: "Crea. Adapta. Enseña. Conecta.",
      createActivity: "Crear una Actividad",
      planWeek: "Planear Mi Semana",
      login: "Iniciar sesión",
      signup: "Comenzar",
      developedBy: "Desarrollado por Brilla Educational Consulting LLC",
    },
    auth: {
      loginTitle: "Bienvenido de nuevo",
      loginSubtitle: "Inicia sesión en tu cuenta de Brilla",
      registerTitle: "Crea tu cuenta",
      registerSubtitle: "Empieza a dar vida a dos idiomas",
      name: "Nombre completo",
      email: "Correo electrónico",
      password: "Contraseña",
      role: "Soy...",
      roleTeacher: "Maestro/a",
      roleCoach: "Entrenador / Gerente de Educación",
      roleAdmin: "Administrador/a",
      loginBtn: "Iniciar sesión",
      registerBtn: "Crear cuenta",
      forgot: "¿Olvidaste tu contraseña?",
      noAccount: "¿No tienes una cuenta?",
      haveAccount: "¿Ya tienes una cuenta?",
      forgotTitle: "Restablece tu contraseña",
      forgotSubtitle: "Ingresa tu correo y te enviaremos un enlace",
      sendReset: "Enviar enlace",
      resetTitle: "Establece una nueva contraseña",
      newPassword: "Nueva contraseña",
      resetBtn: "Restablecer contraseña",
    },
    dashboard: {
      welcome: "Bienvenido de nuevo",
      subtitle: "¿Qué te gustaría crear hoy?",
      quickActions: "Acciones Rápidas",
      recent: "Creadas Recientemente",
      favorites: "Favoritos",
      noRecent: "Aún no hay actividades. ¡Crea la primera!",
      currentClassroom: "Salón Actual",
      ageGroup: "Grupo de Edad",
      weeklyTheme: "Tema Semanal",
      notSet: "Sin definir",
      cards: {
        createActivity: "Crear una Actividad",
        createActivityDesc: "Genera una actividad bilingüe individual.",
        planWeek: "Planear Mi Semana",
        planWeekDesc: "Genera actividades conectadas por un tema semanal.",
        makeBilingual: "Hacer Mi Actividad Bilingüe",
        makeBilingualDesc: "Transforma una actividad existente en una experiencia bilingüe.",
        centers: "Centros de Aprendizaje",
        centersDesc: "Genera actividades bilingües para centros.",
        book: "Compañero de Libro",
        bookDesc: "Crea actividades bilingües conectadas a un libro.",
        family: "Conexión Familiar",
        familyDesc: "Genera actividades bilingües para el hogar.",
        myActivities: "Mis Actividades",
        myActivitiesDesc: "Accede a tus actividades guardadas.",
        weeklyPlans: "Mis Planes Semanales",
        weeklyPlansDesc: "Accede a tus planes semanales guardados.",
        resources: "Biblioteca de Recursos",
        resourcesDesc: "Accede a recursos bilingües de enseñanza.",
      },
      comingSoon: "Próximamente",
    },
    generator: {
      title: "Generador de Actividades Bilingües con IA",
      subtitle: "Cuéntale a Brilla sobre tu actividad y creará un plan completo.",
      ageGroup: "Grupo de Edad",
      language: "Idioma",
      theme: "Tema",
      themePlaceholder: "Escribe tu propio tema o elige un ejemplo abajo",
      domain: "Área de Aprendizaje",
      domainHint: "Elige una o más",
      activityType: "Tipo de Actividad",
      duration: "Duración",
      generateBtn: "Generar Actividad",
      generating: "Brilla está creando tu actividad...",
    },
    activity: {
      objective: "Objetivo",
      materials: "Materiales",
      vocabulary: "Vocabulario Clave",
      teacherLanguage: "Lenguaje del Maestro",
      instructions: "Instrucciones Paso a Paso",
      questions: "Preguntas de Pensamiento de Orden Superior",
      dll: "Estrategias de Apoyo para DLL",
      differentiation: "Diferenciación",
      diffSupport: "Necesita apoyo adicional",
      diffEnglish: "Aprendices principiantes de inglés",
      diffSpanish: "Aprendices principiantes de español",
      diffChallenge: "Necesita mayor desafío",
      observation: "Oportunidades de Observación",
      family: "Conexión Familiar",
      teacherSays: "El maestro dice",
      disclaimer:
        "El contenido educativo generado por IA debe ser revisado por el educador antes de usarlo en el salón.",
      saved: "Actividad guardada en Mis Actividades.",
    },
    makeBilingual: {
      title: "Hacer Mi Actividad Bilingüe",
      subtitle:
        "Pega tu actividad existente. Brilla no solo la traducirá: diseñará una experiencia de aprendizaje bilingüe intencional.",
      inputLabel: "Tu actividad existente",
      inputPlaceholder: "Pega o escribe tu lección o actividad aquí...",
      ageGroup: "Grupo de Edad (opcional)",
      transformBtn: "Hacerla Bilingüe",
      original: "Actividad Original",
      enhanced: "Actividad Bilingüe Mejorada por Brilla",
    },
    ask: {
      title: "Pregúntale a Brilla",
      subtitle: "Tu entrenador instruccional bilingüe y solidario.",
      placeholder: "Pregunta sobre actividades bilingües, vocabulario, apoyo DLL...",
      send: "Enviar",
      thinking: "Brilla está pensando...",
      suggestions: [
        "Dame cinco preguntas bilingües sobre los insectos.",
        "¿Cómo puedo agregar vocabulario en español a mi centro de bloques?",
        "Crea una actividad de círculo apropiada para niños pequeños.",
        "Dame una actividad familiar sobre las formas.",
      ],
    },
    myActivities: {
      title: "Mis Actividades",
      all: "Todas",
      favorites: "Favoritos",
      empty: "No se encontraron actividades. ¡Crea una!",
      filters: "Filtros",
    },
    profile: {
      title: "Perfil de Mi Salón",
      subtitle: "Brilla usa esto para personalizar recomendaciones. Nunca ingreses nombres ni información personal de los niños.",
      teacherName: "Nombre del Maestro",
      classroomName: "Nombre del Salón",
      program: "Programa / Sitio",
      ageGroup: "Grupo de Edad",
      numChildren: "Número de Niños",
      primaryLanguage: "Idioma Principal del Salón",
      languagesSpoken: "Idiomas que Hablan los Niños",
      curriculum: "Currículo Utilizado",
      currentTheme: "Tema Actual",
      goals: "Metas Instruccionales",
    },
    onboarding: {
      next: "Siguiente",
      back: "Atrás",
      finish: "Comenzar",
      skip: "Omitir recorrido",
      steps: [
        { title: "¡Bienvenido a Brilla!", body: "Tu compañero de planificación bilingüe." },
        { title: "Cuéntanos sobre tu salón", body: "Configura tu grupo de edad, preferencias de idioma y currículo para que Brilla personalice todo." },
        { title: "Crea tu primera actividad", body: "Descubre lo rápido que puedes generar una actividad bilingüe completa." },
        { title: "Hazla tuya", body: "Edita, guarda, imprime y regenera cualquier actividad en segundos." },
        { title: "Pregúntale a Brilla", body: "Chatea con tu entrenador de IA cuando necesites ideas o apoyo." },
      ],
    },
  },
};
